"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { ArrowRight, Loader, Phone, Calendar, Calculator } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ReservationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border-t border-border pt-10">
        <p className="font-mono uppercase text-xs tracking-[0.22em] text-muted-foreground mb-4">
          § Confirmation
        </p>
        <p className="font-default font-light text-2xl md:text-3xl text-foreground leading-tight mb-8">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg border border-foreground bg-transparent text-foreground hover:bg-foreground hover:text-background font-default uppercase tracking-[0.18em] text-xs px-8 py-4 h-auto">
          New Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <div className="space-y-6">
        <div className="flex flex-wrap gap-4">
          <Button asChild className="bg-primary text-white hover:bg-primary/90 font-default uppercase tracking-[0.18em] text-xs px-6 py-4 h-auto rounded-lg border border-primary">
            <Link to="https://metallineexteriors.ca/contact/">
              <Calculator className="size-4 mr-2" />
              Get Your Free Estimate
            </Link>
          </Button>
          <Button type="button" variant="outline" onClick={() => {
          const firstInput = document.querySelector("#roofing-inquiry input, #roofing-inquiry select, #roofing-inquiry textarea");
          if (firstInput) {
            firstInput.scrollIntoView({
              behavior: "smooth",
              block: "center"
            });
            (firstInput as HTMLElement).focus();
          }
        }} className="border-foreground text-foreground hover:bg-foreground hover:text-background font-default uppercase tracking-[0.18em] text-xs px-6 py-4 h-auto rounded-lg">
            <SafeHydrate><Calendar className="size-4 mr-2" /></SafeHydrate>
            Book a Consultation
          </Button>
          <Button asChild variant="ghost" className="text-foreground hover:bg-accent font-default uppercase tracking-[0.18em] text-xs px-6 py-4 h-auto rounded-lg">
            <Link to="tel:+15878884223">
              <Phone className="size-4 mr-2" />
              Call (587) 888-4223
            </Link>
          </Button>
        </div>
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Free consultation, no obligation
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
        <FormField control={control} name="date" render={({
        field
      }) => <FormItem className="space-y-3">
              <FormLabel className="font-mono uppercase text-[0.6875rem] tracking-[0.22em] text-muted-foreground">
                Preferred Date (optional)
              </FormLabel>
              <FormControl>
                <Input type="date" {...field} className="border-0 border-b-2 border-border bg-transparent px-0 h-12 md:h-10 w-full focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary font-default text-foreground rounded-lg" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="project_type" rules={{
        required: "Project type is required"
      }} render={({
        field
      }) => <FormItem className="space-y-3">
              <FormLabel className="font-mono uppercase text-[0.6875rem] tracking-[0.22em] text-muted-foreground">
                Project Type *
              </FormLabel>
              <FormControl>
                <Input type="text" placeholder="e.g. Metal Roofing" {...field} className="border-0 border-b-2 border-border bg-transparent px-0 h-12 md:h-10 w-full focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary font-default text-foreground placeholder:text-muted-foreground rounded-lg" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
        <FormField control={control} name="location" render={({
        field
      }) => <FormItem className="space-y-3">
              <FormLabel className="font-mono uppercase text-[0.6875rem] tracking-[0.22em] text-muted-foreground">
                City (optional)
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="border-0 border-b-2 border-border bg-transparent px-0 h-12 md:h-10 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary font-default text-foreground w-full rounded-lg">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="rounded-lg">
                  <SelectItem value="calgary">Calgary</SelectItem>
                  <SelectItem value="airdrie">Airdrie</SelectItem>
                  <SelectItem value="okotoks">Okotoks</SelectItem>
                  <SelectItem value="cochrane">Cochrane</SelectItem>
                  <SelectItem value="chestermere">Chestermere</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem className="space-y-3">
              <FormLabel className="font-mono uppercase text-[0.6875rem] tracking-[0.22em] text-muted-foreground">
                Phone *
              </FormLabel>
              <FormControl>
                <Input type="tel" inputMode="tel" autoComplete="tel" placeholder="Your Phone Number" {...field} className="border-0 border-b-2 border-border bg-transparent px-0 h-12 md:h-10 w-full focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary font-default text-foreground placeholder:text-muted-foreground rounded-lg" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="postal_code" rules={{
        required: "Postal code is required"
      }} render={({
        field
      }) => <FormItem className="space-y-3">
              <FormLabel className="font-mono uppercase text-[0.6875rem] tracking-[0.22em] text-muted-foreground">
                Postal Code *
              </FormLabel>
              <FormControl>
                <Input type="text" autoComplete="postal-code" placeholder="Postal Code (e.g. T2P 1J9)" {...field} className="border-0 border-b-2 border-border bg-transparent px-0 h-12 md:h-10 w-full focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary font-default text-foreground placeholder:text-muted-foreground rounded-lg" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="preferred_roofing_type" rules={{
      required: "Please select a roofing type"
    }} render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="font-mono uppercase text-[0.6875rem] tracking-[0.22em] text-muted-foreground">
              Preferred Roofing Type *
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="border-0 border-b-2 border-border bg-transparent px-0 h-12 md:h-10 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary font-default text-foreground w-full rounded-lg">
                  <SelectValue placeholder="Select a roofing system" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="rounded-lg">
                <SelectItem value="metal-shingles">Metal Shingles Roofing</SelectItem>
                <SelectItem value="standing-seam">Standing Seam Metal Roofing</SelectItem>
                <SelectItem value="stone-coated">Stone-Coated Steel Roofing</SelectItem>
                <SelectItem value="not-sure">Not Sure – Help Me Choose</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="full_name" rules={{
      required: "Full name is required"
    }} render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="font-mono uppercase text-[0.6875rem] tracking-[0.22em] text-muted-foreground">
              Full Name *
            </FormLabel>
            <FormControl>
              <Input type="text" placeholder="Your Name" {...field} className="border-0 border-b-2 border-border bg-transparent px-0 h-12 md:h-10 w-full focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary font-default text-foreground placeholder:text-muted-foreground rounded-lg" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      validate: value => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || "Please enter a valid email address"
    }} render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="font-mono uppercase text-[0.6875rem] tracking-[0.22em] text-muted-foreground">
              Email (optional)
            </FormLabel>
            <FormControl>
              <Input type="email" inputMode="email" autoComplete="email" placeholder="john@example.com" {...field} className="border-0 border-b-2 border-border bg-transparent px-0 h-12 md:h-10 w-full focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary font-default text-foreground placeholder:text-muted-foreground rounded-lg" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="special_requests" render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="font-mono uppercase text-[0.6875rem] tracking-[0.22em] text-muted-foreground">
              Project Details (optional)
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your exterior renovation needs..." {...field} className="border-0 border-b-2 border-border bg-transparent px-0 py-2 min-h-20 w-full focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary font-default text-foreground placeholder:text-muted-foreground resize-none rounded-lg" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="guest_list" render={({
      field
    }) => <FormItem className="flex flex-col gap-2 space-y-0">
            <div className="flex items-center gap-3">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} className="rounded-lg border-foreground" />
              </FormControl>
              <FormLabel className="font-mono uppercase text-[0.6875rem] tracking-[0.22em] text-muted-foreground cursor-pointer">
                Subscribe to our newsletter
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="border-t border-destructive pt-4">
          <p className="font-mono uppercase text-xs tracking-[0.18em] text-destructive">
            {submitError}
          </p>
        </div>}

      <div className="pt-4">
        <div className="flex items-center mb-8">
          <div className="h-px w-24 bg-primary" />
          <div className="size-1 bg-primary" />
        </div>

        <div className="flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-6">
          <Link to="tel:+15878884223" className="font-mono uppercase text-xs tracking-[0.18em] text-foreground group inline-flex items-center">
            <span className="relative">
              Or call (587) 888-4223
              <span className="absolute left-0 -bottom-1 h-px w-0 bg-foreground transition-all duration-[350ms] group-hover:w-full" />
            </span>
          </Link>

          <div className="flex flex-col items-start md:items-end gap-2.5">
            <p className="text-xs text-muted-foreground">
              We use the details you submit to respond to your inquiry and prepare your quote. Read our{" "}
              <Link to="/PrivacyPolicyPage" className="underline underline-offset-4 text-foreground hover:text-primary transition-colors">
                Privacy Policy
              </Link>
              .
            </p>

            <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-h-[48px] h-12 md:h-auto bg-primary text-white hover:bg-primary/90 font-default uppercase tracking-[0.18em] text-xs px-8 py-4 border border-primary transition-colors duration-[250ms] rounded-lg flex items-center justify-center">
              {isSubmitting ? <>
                  <Loader className="animate-spin mr-2 size-4" />
                  Sending...
                </> : <>
                  Request Quote
                  <ArrowRight className="ml-2 size-4" />
                </>}
            </Button>
          </div>
        </div>
      </div>
    </Form>;
}
export default function Inquiry() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="1344" id="roofing-inquiry" className="relative bg-background text-foreground overflow-hidden ![padding:50px_0px_30px_0px]">
      <div ref={ref} className={`relative max-w-[1440px] mx-auto px-6 md:px-16 transition-all duration-[700ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div aria-hidden="true" className="pointer-events-none absolute -left-8 md:-left-16 top-24 select-none font-default font-thin text-foreground/[0.08] leading-none" style={{
        fontSize: "clamp(280px, 42vw, 620px)"
      }}>
          01
        </div>

        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5 lg:pr-8">
            <div className="flex items-center gap-4 mb-12">
              <span className="font-mono uppercase text-[0.6875rem] tracking-[0.22em] text-muted-foreground">REQUEST A QUOTE</span>
            </div>

            <div className="flex items-center mb-12">
              <div className="h-px w-24 bg-primary" />
              <div className="size-1 bg-primary" />
            </div>

            <h1 className="font-default font-bold text-foreground leading-[0.92] ![margin:0px_12px_48px_0px] !text-6xl" style={{
            fontSize: "clamp(3.25rem, 8.4vw, 9.5rem)",
            letterSpacing: "-0.045em"
          }}>
              Metal Roofing Solutions for Your Calgary Home
            </h1>

            <p className="font-default font-light text-muted-foreground max-w-md ![margin:20px_0px_64px_0px]" style={{
            fontSize: "clamp(1.125rem, 1.4vw, 1.375rem)",
            lineHeight: 1.5
          }}>
              MetalLine Exteriors delivers premium metal roofing and architectural cladding solutions. Request your consultation today and secure your home with industry-leading craftsmanship.
            </p>

            <div className="space-y-0">
              <div className="border-t border-border py-5 grid grid-cols-2 gap-4">
                <span className="font-mono uppercase text-[0.6875rem] tracking-[0.22em] text-muted-foreground">
                  Service Area
                </span>
                <span className="font-mono uppercase text-[0.6875rem] tracking-[0.22em] text-foreground">
                  Calgary & Surrounding
                </span>
              </div>
              <div className="border-t border-border py-5 grid grid-cols-2 gap-4">
                <span className="font-mono uppercase text-[0.6875rem] tracking-[0.22em] text-muted-foreground">
                  Expertise
                </span>
                <span className="font-mono uppercase text-[0.6875rem] tracking-[0.22em] text-foreground">
                  Metal, Siding, Stone
                </span>
              </div>
              <div className="border-t border-b border-border py-5 grid grid-cols-2 gap-4">
                <span className="font-mono uppercase text-[0.6875rem] tracking-[0.22em] text-muted-foreground">
                  Consultation
                </span>
                <span className="font-mono uppercase text-[0.6875rem] tracking-[0.22em] text-foreground">
                  Available Daily
                </span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 relative">
            <div className="lg:border-l lg:border-border lg:pl-12">
              <FormProvider formKey="roofing_inquiry_form_roofing" formId="roofing_inquiry_form_roofing" sectionName="roofing-inquiry" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              postal_code: "",
              date: "",
              project_type: "",
              location: "",
              preferred_roofing_type: "",
              special_requests: "",
              guest_list: false
            }} validationRules={{
              full_name: {
                required: true,
                minLength: 2
              },
              email: {
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              postal_code: {
                required: true
              },
              project_type: {
                required: true
              },
              preferred_roofing_type: {
                required: true
              }
            }} submitText="Request Quote" successMessage="Thank you! Your request has been submitted successfully. We will contact you shortly." errorMessage="We couldn't process your request. Please try again or call us directly.">
                <ReservationForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}